import { z } from 'zod';
import { MUSCLE_GROUPS, adminExerciseCreateRequestSchema } from './exercises.js';
import { cursorPageQuerySchema } from './pagination.js';
/**
 * Exercises the catalog lacks, as gym equipment scans reveal them.
 *
 * When an equipment scan offers an exercise Steado has no row for, the model
 * writes it itself and the app serves it labelled "not yet reviewed". Every
 * such exercise is counted here under its normalised name, **one row per
 * name rather than one per person** (unlike food requests): nobody asks for
 * these, scans reveal them, and what decides which one an operator looks at
 * first is how many people hit the gap.
 *
 * An operator answers a request one of three ways:
 *
 * - **Create from draft**: a strong model drafts the catalog row ("Draft with
 *   AI"), the operator reviews it in the ordinary create form, and the new row
 *   resolves the request (`created`).
 * - **This is it**: the catalog already has the movement under another name.
 *   Resolving to it (`existing`) also records the request's name as one of the
 *   exercise's aliases, so the next scan that writes that name gets the
 *   catalog row instead of an AI one.
 * - **Reject**: not worth a catalog row. Later scans keep counting.
 */
export const EXERCISE_REQUEST_STATUSES = ['open', 'resolved', 'rejected'];
/**
 * Where the AI draft stands. `queued` covers a job waiting or running;
 * `failed` means every attempt failed, and "Draft with AI" may be pressed
 * again. A stale `ready` draft may be redrafted too.
 */
export const EXERCISE_DRAFT_STATUSES = ['none', 'queued', 'ready', 'failed'];
/**
 * `reach` (the default) puts the gap the most people hit first: distinct
 * users, then scans, then the most recently seen. `recent` is the plain
 * newest-first order, for checking what the latest scans turned up.
 */
export const EXERCISE_REQUEST_SORTS = ['reach', 'recent'];
/** A new catalog row made for the request, or one Steado already had. */
export const EXERCISE_REQUEST_RESOLUTIONS = ['created', 'existing'];
/**
 * How a catalog row relates to the request, in the order the console lists
 * them. The first three and `different` are the drafting model's judgement
 * (`same` is the row it called a duplicate); `similar_name` is a row whose
 * name merely overlaps the request's and nobody has judged.
 */
export const EXERCISE_CANDIDATE_RELATIONS = ['same', 'variant', 'similar_name', 'different'];
/** The most scans a request's detail shows as evidence. */
export const EXERCISE_REQUEST_SAMPLE_LIMIT = 6;
export const adminExerciseRequestSchema = z.object({
    id: z.string().uuid(),
    /** The latest spelling a scan used. */
    name: z.string(),
    /** The normalised name every scan of this exercise shares. */
    nameKey: z.string(),
    /** The equipment it was last offered on. */
    equipmentName: z.string().nullable(),
    scanCount: z.number().int(),
    /** Distinct people whose scans produced it: the reach. */
    userCount: z.number().int(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
    status: z.enum(EXERCISE_REQUEST_STATUSES),
    draftStatus: z.enum(EXERCISE_DRAFT_STATUSES),
    /**
     * The catalog row it was resolved to. Null while open or rejected, and null
     * again if that row is ever deleted: the FK is SetNull, because the record
     * that people kept scanning for it outlives the row.
     */
    resolvedExercise: z.object({ id: z.string().uuid(), name: z.string(), slug: z.string() }).nullable(),
    resolution: z.enum(EXERCISE_REQUEST_RESOLUTIONS).nullable(),
    rejectionReason: z.string().nullable(),
});
/**
 * The model's catalog row, in the create form's own shape, every field
 * optional: the API checks each field against the create schema and drops
 * the ones that fail, with a sentence in `warnings` for each, so one bad
 * field costs that field and not the draft. `sortOrder` is always 500, the
 * curated rows' order, rather than anything the model said.
 */
export const adminExerciseDraftExerciseSchema = adminExerciseCreateRequestSchema.partial();
export const adminExerciseRequestDraftSchema = z.object({
    exercise: adminExerciseDraftExerciseSchema,
    /** What was dropped or needs a person's eye, one sentence each. */
    warnings: z.array(z.string()),
    /** Anything the model wanted the reviewer to know. */
    notes: z.string().nullable(),
    model: z.string().nullable(),
    draftedAt: z.string(),
});
/** A catalog row the request might be: "Steado may already have this". Active and shared only. */
export const adminExerciseRequestCandidateSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    relation: z.enum(EXERCISE_CANDIDATE_RELATIONS),
    /** The model's reason, when it gave one. */
    note: z.string().nullable(),
});
/**
 * One scan that produced the request, newest first. The steps and muscles are
 * what the model wrote at the machine and what the user was shown.
 *
 * The photo itself is not here. It can show other people at the gym, so it is
 * fetched on an explicit click from its own admin-only, audited route, and
 * `hasPhoto` only says whether there is one to fetch (it is deleted with the
 * scan, or after the retention window).
 */
export const adminExerciseRequestSampleSchema = z.object({
    scanId: z.string().uuid(),
    itemId: z.string().uuid(),
    capturedAt: z.string(),
    equipmentName: z.string().nullable(),
    howToSteps: z.array(z.string()),
    primaryMuscles: z.array(z.enum(MUSCLE_GROUPS)),
    hasPhoto: z.boolean(),
});
export const adminExerciseRequestDetailSchema = adminExerciseRequestSchema.extend({
    /** The stored, already-checked draft. Null until one is READY. */
    draft: adminExerciseRequestDraftSchema.nullable(),
    /** Why the last draft failed, when `draftStatus` is `failed`. Short, never a stack. */
    draftError: z.string().nullable(),
    draftRequestedAt: z.string().nullable(),
    candidates: z.array(adminExerciseRequestCandidateSchema),
    samples: z.array(adminExerciseRequestSampleSchema),
});
export const adminExerciseRequestListQuerySchema = cursorPageQuerySchema.extend({
    status: z.enum(EXERCISE_REQUEST_STATUSES).optional(),
    sort: z.enum(EXERCISE_REQUEST_SORTS).default('reach'),
});
export const adminExerciseRequestParamsSchema = z.object({ id: z.string().uuid() });
export const adminExerciseRequestSampleParamsSchema = z.object({
    id: z.string().uuid(),
    scanId: z.string().uuid(),
});
/**
 * Pointing the request at a catalog row. `created` says the row was made for
 * this request (Create from draft) rather than found (This is it); it is
 * recorded, not checked.
 */
export const adminExerciseRequestResolveRequestSchema = z.object({
    exerciseId: z.string().uuid(),
    created: z.boolean(),
});
/** Required, unlike a food request's: the next operator to meet the name needs to know why. */
export const adminExerciseRequestRejectRequestSchema = z.object({
    reason: z.string().trim().min(1).max(500),
});
//# sourceMappingURL=exercise-requests.js.map